import { createApp, ref } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { useAuthStore } from './stores/auth';
import { createI18nInstance } from './i18n';
import "./style/index.css";
import 'flag-icon-css/css/flag-icons.min.css';
import vue3GoogleLogin from 'vue3-google-login';
import vueCountryRegionSelect from 'vue3-country-region-select';
import router from './router';
import axios from 'axios';
import { googleClientId } from './config';
import Cookies from 'js-cookie';
import { useI18nStore } from '@/stores/i18nStore';
import { LoadingPlugin } from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { logger } from '@/utils/logger';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, getSupportedLanguageOrDefault } from '@/config/languages';

// 導入需要預載的圖標
import microsoftIcon from '@/assets/svg/icon_48px_Microsoft.svg';
import googleIcon from '@/assets/svg/icon_48px_Google.svg';
import appleIcon from '@/assets/svg/icon_48px_Apple.svg';
import passkeyIcon from '@/assets/svg/icon_32px_passkey.svg';
import infoIcon from '@/assets/svg/icon_24px_info_green.svg';
import logoIcon from '@/assets/svg/logo_Zyxel networks.svg';
import languageIcon from '@/assets/svg/icon_48px_language.svg';

const store = createPinia();
const app = createApp(App);

app.use(vue3GoogleLogin, {
  clientId: googleClientId,
  idConfiguration: {
    use_fedcm_for_prompt: true,
  },
});

app.use(LoadingPlugin);
app.use(vueCountryRegionSelect);
app.use(router).use(store);

const i18n = createI18nInstance();
app.use(i18n);

// 定義模糊匹配的關鍵字
const SENSITIVE_KEYWORDS = ["password", "new_password", "old_password"];

axios.interceptors.response.use(
  response => response,
  error => {
    const auth = useAuthStore();
    if (error.response && error.response.status === 401) {
      auth.purgeUser();
      router.push('/users/sign_in');
    }
    if (error.config && error.config.data) {
      try {
        const data = JSON.parse(error.config.data);

        // 遍歷 data 的鍵名並隱藏敏感資訊
        for (const key in data) {
          if (SENSITIVE_KEYWORDS.some(keyword => key.toLowerCase().includes(keyword))) {
            data[key] = '******'; // 隱藏敏感資訊
          }
        }

        // 將修改後的 data 回寫到 config
        error.config.data = JSON.stringify(data);
      } catch (e) {
        // 如果 data 無法解析為 JSON，記錄錯誤或清空資料
        logger.warn('Invalid JSON in error.config.data:', error.config.data);
        error.config.data = '{}';
      }
    }
    return Promise.reject(error);
  }
);

const languageLoaded = ref(false);
app.config.globalProperties.$languageLoaded = languageLoaded;

// 初始設定 body 為完全透明但「在渲染樹中」
// 這樣 Chrome 會在後台進行字體計算，而 visibility: hidden 則會延遲計算
document.body.style.setProperty('opacity', '0');
document.body.style.setProperty('pointer-events', 'none'); // 防止透明時誤點

router.isReady().then(async () => {
  await initializeLanguage();
});

// 預載並預解碼圖片的工具函式
const preloadImage = (src) => {
  return new Promise((resolve) => {
    if (!src) return resolve();
    const img = new Image();
    img.src = src;
    img.onload = () => {
      if (img.decode) {
        img.decode().then(resolve).catch(() => resolve());
      } else {
        resolve();
      }
    };
    img.onerror = () => resolve();
  });
};

async function initializeLanguage() {
  const authStore = useAuthStore();
  const i18nStore = useI18nStore();
  let selectedLocale = DEFAULT_LANGUAGE;

  await authStore.fetchCurrentUser();
  if (authStore.isAuthenticated && authStore.current_user && authStore.current_user.display_language) {
    selectedLocale = getSupportedLanguageOrDefault(authStore.current_user.display_language);
  } else {
    const routeLocale = router.currentRoute.value.query.locale;
    const cookieLocale = Cookies.get('v3_locale');
    const browserLocale = navigator.language;

    if (routeLocale && SUPPORTED_LANGUAGES[routeLocale]) {
      selectedLocale = routeLocale;
    } else if (cookieLocale && SUPPORTED_LANGUAGES[cookieLocale]) {
      selectedLocale = cookieLocale;
    } else if (browserLocale && SUPPORTED_LANGUAGES[browserLocale]) {
      selectedLocale = browserLocale;
    }
  }

  try {
    await i18nStore.loadLanguage(selectedLocale);
    i18nStore.setLocale(selectedLocale);
    i18n.global.locale.value = selectedLocale;
    document.documentElement.setAttribute('lang', selectedLocale);

    // 平行等待字體與圖標
    const fontPromises = [];
    if (document.fonts) {
      // 明確要求載入所有粗度與大小的字體，確保標題與內文同時準備好
      fontPromises.push(document.fonts.load('bold 28pt Inter-Bold-28pt'));
      fontPromises.push(document.fonts.load('bold 24pt Inter-Bold-24pt'));
      fontPromises.push(document.fonts.load('bold 18pt Inter-Bold-18pt'));
      fontPromises.push(document.fonts.load('300 18pt Inter-Light-18pt'));
      fontPromises.push(document.fonts.ready);
    }

    const currentPath = router.currentRoute.value.path;
    const isSignInPage = ['/users/sign_in'].includes(currentPath);

    if (isSignInPage) {
      await Promise.all([
        ...fontPromises,
        preloadImage(microsoftIcon),
        preloadImage(googleIcon),
        preloadImage(appleIcon),
        preloadImage(passkeyIcon),
        preloadImage(infoIcon),
        preloadImage(logoIcon),
        preloadImage(languageIcon)
      ]);
    } else {
      preloadImage(microsoftIcon);
      await Promise.all(fontPromises);
    }

    // 掛載 App
    languageLoaded.value = true;
    app.mount('#app');

    // 針對 Chrome 的終極同步機制
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // 強制觸發瀏覽器重繪 (Reflow)，確保字體佈局計算完成
        void document.body.offsetHeight;

        setTimeout(() => {
          document.body.style.removeProperty('opacity');
          document.body.style.removeProperty('pointer-events');
        }, 50); // 縮短至 50ms，提升畫面出現的速度
      });
    });

  } catch (error) {
    logger.error("Error initializing language:", error);
    try {
      await i18nStore.loadLanguage(DEFAULT_LANGUAGE);
      i18nStore.setLocale(DEFAULT_LANGUAGE);
      i18n.global.locale.value = DEFAULT_LANGUAGE;
      document.documentElement.setAttribute('lang', DEFAULT_LANGUAGE);

      app.mount('#app');
      document.body.style.removeProperty('opacity');
      document.body.style.removeProperty('pointer-events');
    } catch (fallbackError) {
      logger.error("Error loading fallback language:", fallbackError);
      app.mount('#app');
      document.body.style.removeProperty('opacity');
      document.body.style.removeProperty('pointer-events');
    }
  }
}
